import React from "react";
import { Card, Form, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { StatusBadge, PriorityBadge } from "./Badges/TaskBadges";
import {
  CloseIcon,
  MapMarkerAltIcon,
  MenuAlt1Icon,
  LineChartIcon,
  MoreIcon,
  EditIcon,
  TrashIcon,
} from "@components/icons";
import { REGELKONTROLLE_GROUPS } from "@modules/Trees/constants/sichtkontrolle";
import { parentTreeIcon } from "@helper/marker";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const DetailRow = ({ label, children }) => (
  <div className="entity-detail-data-row">
    <div className="entity-detail-data-label">{label}</div>
    <div className="entity-detail-data-value">{children}</div>
  </div>
);

const formatBoolean = (value, t) => {
  if (typeof value !== "boolean") return "-";
  return value ? t("care_chat.yes") : t("care_chat.no");
};

export default function TaskDetail({ task, tree, onClose, onDelete, onEditRequest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/tasks");
  };
  const handleEdit = () => {
    if (!task) return;
    onEditRequest?.(task);
  };
  const handleDelete = () => {
    if (!task?.id) return;
    onDelete?.(task.id);
  };

  const treeId = task?.tree?.id || task?.treeId || null;
  const mapUrl = treeId ? `/map?selectedTree=${treeId}` : null;
  const listUrl = treeId ? `/trees?selectedTree=${treeId}` : null;
  const dsUrl = treeId ? `/data-science?trees=${treeId}` : "/data-science";
  const treeLat =
    Number(tree?.position?.lat) || (Array.isArray(tree?.latlng) ? Number(tree.latlng[0]) : NaN);
  const treeLng =
    Number(tree?.position?.lng) || (Array.isArray(tree?.latlng) ? Number(tree.latlng[1]) : NaN);
  const hasTreePosition = Number.isFinite(treeLat) && Number.isFinite(treeLng);
  const measureType = String(task?.measureType || task?.title || "");
  const data = task?.data || {};
  const irrigationLog = data?.irrigationLog || null;
  const pruningLog = data?.pruningLog || null;
  const checklistMeta = data?.sichtkontrolleMeta || null;
  const furtherAction = checklistMeta?.weiteresVorgehen || null;
  const inspectionMode = checklistMeta?.regelkontrolleMode || "form";
  const regelkontrolleDocument = data?.regelkontrolleDocument || null;
  const checklist = data?.sichtkontrolleChecks || null;
  const checklistCheckedCount = checklist ? Object.values(checklist).filter(Boolean).length : 0;
  const checklistGroups = REGELKONTROLLE_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      checked: Boolean(checklist?.[item.id]),
    })),
  }));
  const isIrrigation =
    measureType === "Bewässerung" || measureType === "Gießmaßnahme" || Boolean(irrigationLog);
  const isPruning = measureType === "Schnittmaßnahme" || Boolean(pruningLog);
  const isInspection = measureType === "Regelkontrolle";

  return (
    <>
      <Card className="h-100 task-detail-panel entity-detail-panel">
        <Card.Header className="task-detail-panel-header entity-detail-header">
          <div className="d-flex align-items-center justify-content-between gap-2 w-100 entity-detail-header-row">
            <button
              type="button"
              className="task-detail-close-btn entity-detail-control"
              onClick={handleClose}
              aria-label={t("close")}
              title={t("close")}
            >
              <CloseIcon />
            </button>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <h5 className="mb-0 task-detail-panel-title entity-detail-title text-truncate">
                {measureType || task?.title || t("task_details")}
              </h5>
            </div>
            <div className="d-flex align-items-center gap-2 task-detail-panel-actions">
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="button"
                  className="task-detail-icon-link task-detail-actions-toggle entity-detail-control entity-detail-actions-toggle border-0"
                  id="task-detail-actions-dropdown"
                  title={t("more_actions", "Mehr Aktionen")}
                >
                  <MoreIcon size="24px" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Header className="task-detail-actions-section-label">
                    {t("task_details", "Aufgabendetails")}
                  </Dropdown.Header>
                  <Dropdown.Item onClick={handleEdit}>
                    <EditIcon width={"16px"} /> {t("edit", "Bearbeiten")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleDelete}>
                    <TrashIcon width={"16px"} /> {t("delete", "Löschen")}
                  </Dropdown.Item>
                  {(mapUrl || listUrl || dsUrl) && <Dropdown.Divider />}
                  {(mapUrl || listUrl || dsUrl) && (
                    <Dropdown.Header className="task-detail-actions-section-label">
                      {t("tree", "Baum")}
                    </Dropdown.Header>
                  )}
                  {mapUrl && (
                    <Dropdown.Item as={Link} to={mapUrl}>
                      <MapMarkerAltIcon width={"16px"} /> {t("view_on_map", "Zur Karte")}
                    </Dropdown.Item>
                  )}
                  {listUrl && (
                    <Dropdown.Item as={Link} to={listUrl}>
                      <MenuAlt1Icon width={"16px"} /> {t("view_on_list", "Zur Liste")}
                    </Dropdown.Item>
                  )}
                  {dsUrl && (
                    <Dropdown.Item as={Link} to={dsUrl}>
                      <LineChartIcon width={"18px"} /> {t("view_on_chart", "Zu Data Science")}
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="task-detail-panel-body entity-detail-body">
          <div className="entity-detail-data">
            <DetailRow label={t("task")}>{measureType || "-"}</DetailRow>
            <DetailRow label={t("status")}>
              <StatusBadge status={task.status}>
                {t(`status_${task.status.replace("-", "_")}`)}
              </StatusBadge>
            </DetailRow>
            <DetailRow label={t("task_priority")}>
              <PriorityBadge priority={task.priority}>
                {t(`priority_${task.priority}`)}
              </PriorityBadge>
            </DetailRow>
            <DetailRow label={t("task_due_date")}>
              {formatDateTime(task.scheduledAt) || "-"}
              {task.isOverdue && <span className="text-danger ms-2">({t("stats_overdue")})</span>}
            </DetailRow>
            <DetailRow label={t("task_assignee")}>{task.assignee?.name || "-"}</DetailRow>
            <DetailRow label={t("tree")}>{task.tree?.species || "-"}</DetailRow>
            <DetailRow label={t("project_name")}>{task.tree?.projectName || "-"}</DetailRow>
            <DetailRow label={t("location")}>{task.tree?.location || "-"}</DetailRow>
            {hasTreePosition && (
              <div className="pt-3">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.9rem",
                    fontSize: "0.92rem",
                    fontWeight: 700,
                    color: "#0e2947",
                  }}
                >
                  <MapMarkerAltIcon width="16px" />
                  <span>{t("location")}</span>
                </div>
                <div
                  style={{
                    height: "180px",
                    width: "100%",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid rgba(40, 108, 165, 0.12)",
                    boxShadow: "0 10px 24px rgba(40, 108, 165, 0.08)",
                    background: "#eef4f8",
                  }}
                >
                  <MapContainer
                    center={[treeLat, treeLng]}
                    zoom={16}
                    scrollWheelZoom={false}
                    style={{ height: "100%", width: "100%" }}
                    dragging={false}
                    doubleClickZoom={false}
                    zoomControl={false}
                    attributionControl={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker
                      position={[treeLat, treeLng]}
                      icon={parentTreeIcon(tree?.health_state)}
                    />
                  </MapContainer>
                </div>
              </div>
            )}
            {task.costCenter && (
              <DetailRow label={t("care_chat.cost_center")}>{task.costCenter}</DetailRow>
            )}
            {data?.beschreibung && (
              <DetailRow label={t("care_chat.description")}>{data.beschreibung}</DetailRow>
            )}

            {isIrrigation && (
              <>
                <DetailRow label={t("care_chat.water_amount_liters")}>
                  {irrigationLog?.waterLiters ?? data?.mengeLiter ?? "-"}
                </DetailRow>
                {(irrigationLog?.method || data?.methode) && (
                  <DetailRow label={t("care_chat.irrigation_method")}>
                    {irrigationLog?.method || data?.methode}
                  </DetailRow>
                )}
                {(irrigationLog?.reason || data?.grund) && (
                  <DetailRow label={t("care_chat.reason")}>
                    {irrigationLog?.reason || data?.grund}
                  </DetailRow>
                )}
                {irrigationLog?.weather && (
                  <DetailRow label={t("care_chat.weather_conditions")}>
                    {irrigationLog.weather}
                  </DetailRow>
                )}
                {irrigationLog?.soilMoisture && (
                  <DetailRow label={t("care_chat.soil_moisture")}>
                    {irrigationLog.soilMoisture}
                  </DetailRow>
                )}
                {irrigationLog?.treeCondition && (
                  <DetailRow label={t("care_chat.tree_condition")}>
                    {irrigationLog.treeCondition}
                  </DetailRow>
                )}
                {irrigationLog?.notes && (
                  <DetailRow label={t("care_chat.remarks")}>{irrigationLog.notes}</DetailRow>
                )}
              </>
            )}

            {isPruning && (
              <>
                {(pruningLog?.type || data?.schnittArt) && (
                  <DetailRow label={t("care_chat.pruning_type")}>
                    {pruningLog?.type || data?.schnittArt}
                  </DetailRow>
                )}
                {(pruningLog?.treeCondition || data?.befund) && (
                  <DetailRow label={t("care_chat.tree_condition")}>
                    {pruningLog?.treeCondition || data?.befund}
                  </DetailRow>
                )}
                {pruningLog?.notes && (
                  <DetailRow label={t("care_chat.remarks")}>{pruningLog.notes}</DetailRow>
                )}
              </>
            )}

            {isInspection && (
              <>
                <DetailRow label={t("care_chat.fill_checklist")}>
                  {checklistCheckedCount > 0 ? `${checklistCheckedCount}` : "-"} /{" "}
                  {inspectionMode === "upload"
                    ? t("care_chat.upload_document")
                    : t("care_chat.fill_form")}
                </DetailRow>
                {inspectionMode !== "upload" && (
                  <DetailRow label={t("care_chat.inspection_title")}>
                    <div className="d-grid gap-2 w-100">
                      {checklistGroups.map((group) => (
                        <div
                          key={group.id}
                          className="task-detail-checklist-group"
                          style={{
                            borderRadius: "8px",
                            padding: "8px 10px",
                          }}
                        >
                          <div className="small fw-semibold mb-1">{group.label}</div>
                          <div className="d-grid gap-1">
                            {group.items.map((item) => (
                              <Form.Check
                                key={item.id}
                                type="checkbox"
                                checked={item.checked}
                                disabled
                                readOnly
                                label={item.label}
                                className="small"
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </DetailRow>
                )}
                {checklistMeta?.kontrolliertVon && (
                  <DetailRow label={t("care_chat.inspected_by")}>
                    {checklistMeta.kontrolliertVon}
                  </DetailRow>
                )}
                {checklistMeta?.kontrolliertAm && (
                  <DetailRow label={t("care_chat.date")}>
                    {formatDateTime(checklistMeta.kontrolliertAm)}
                  </DetailRow>
                )}
                {checklistMeta?.naechsteKontrolle && (
                  <DetailRow label={t("care_chat.next_inspection")}>
                    {formatDateTime(checklistMeta.naechsteKontrolle)}
                  </DetailRow>
                )}
                {checklistMeta?.intervallJahre != null && (
                  <DetailRow label={t("care_chat.interval_years")}>
                    {checklistMeta.intervallJahre}
                  </DetailRow>
                )}
                {typeof checklistMeta?.verkehrssicher === "boolean" && (
                  <DetailRow label={t("care_chat.road_safe")}>
                    {formatBoolean(checklistMeta.verkehrssicher, t)}
                  </DetailRow>
                )}
                {checklistMeta?.bemerkung && (
                  <DetailRow label={t("care_chat.remarks")}>{checklistMeta.bemerkung}</DetailRow>
                )}
                {regelkontrolleDocument?.name && (
                  <DetailRow label={t("care_chat.upload_document")}>
                    {regelkontrolleDocument.name}
                  </DetailRow>
                )}
                {furtherAction && (
                  <>
                    {typeof furtherAction?.handlungsbedarf === "boolean" && (
                      <DetailRow label={t("care_chat.action_required")}>
                        {formatBoolean(furtherAction.handlungsbedarf, t)}
                      </DetailRow>
                    )}
                    {typeof furtherAction?.abstimmungFach === "boolean" && (
                      <DetailRow label={t("care_chat.consult_specialist")}>
                        {formatBoolean(furtherAction.abstimmungFach, t)}
                      </DetailRow>
                    )}
                    {typeof furtherAction?.eingehendeUntersuchung === "boolean" && (
                      <DetailRow label={t("care_chat.detailed_investigation")}>
                        {formatBoolean(furtherAction.eingehendeUntersuchung, t)}
                      </DetailRow>
                    )}
                    {furtherAction?.baumpflegerischeMassnahmen && (
                      <DetailRow label={t("care_chat.tree_care_measures")}>
                        {furtherAction.baumpflegerischeMassnahmen}
                      </DetailRow>
                    )}
                    {furtherAction?.kontrollintervallKuenftig && (
                      <DetailRow label={t("care_chat.interval_future_every")}>
                        {furtherAction.kontrollintervallKuenftig}
                      </DetailRow>
                    )}
                    {typeof furtherAction?.kontrollintervallWieBisher === "boolean" && (
                      <DetailRow label={t("care_chat.interval_as_before")}>
                        {formatBoolean(furtherAction.kontrollintervallWieBisher, t)}
                      </DetailRow>
                    )}
                    {typeof furtherAction?.faellung === "boolean" && (
                      <DetailRow label={t("care_chat.felling")}>
                        {formatBoolean(furtherAction.faellung, t)}
                      </DetailRow>
                    )}
                    {typeof furtherAction?.erledigungSofort === "boolean" && (
                      <DetailRow label={t("care_chat.immediately")}>
                        {formatBoolean(furtherAction.erledigungSofort, t)}
                      </DetailRow>
                    )}
                    {furtherAction?.erledigungWochen && (
                      <DetailRow label={t("care_chat.weeks")}>
                        {furtherAction.erledigungWochen}
                      </DetailRow>
                    )}
                    {furtherAction?.anmerkungen && (
                      <DetailRow label={t("care_chat.remarks_label")}>
                        {furtherAction.anmerkungen}
                      </DetailRow>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
